import { useState, useEffect, useRef } from "react";

export function useMediaQuery(query: string): boolean {
  // Initialize with false for SSR compatibility
  const [matches, setMatches] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const mediaQueryRef = useRef<MediaQueryList | null>(null);

  useEffect(() => {
    // Mark as client-side rendered
    setIsClient(true);
    
    // Only run on client-side
    if (typeof window === "undefined") return;
    
    try {
      const media = window.matchMedia(query);
      mediaQueryRef.current = media;
      
      // Set initial value
      setMatches(media.matches);
      
      const listener = (event: MediaQueryListEvent) => {
        setMatches(event.matches);
      };
      
      // Add listener using the modern API
      if (media.addEventListener) {
        media.addEventListener("change", listener);
      } else {
        // Fallback for older browsers
        media.addListener(listener);
      }
      
      return () => {
        if (mediaQueryRef.current) {
          if (mediaQueryRef.current.removeEventListener) {
            mediaQueryRef.current.removeEventListener("change", listener);
          } else {
            // Fallback for older browsers
            mediaQueryRef.current.removeListener(listener);
          }
        }
      };
    } catch (error) {
      // Handle invalid media queries gracefully
      console.warn(`Invalid media query: ${query}`, error);
      setMatches(false);
      return undefined;
    }
  }, [query]);

  // Return false during SSR to prevent hydration mismatches
  if (!isClient) {
    return false;
  }

  return matches;
}
